// Keeps every column's width identical across all rows of a react-table
// (v6) instance. react-table fakes a table with per-row flexbox — each
// `.rt-tr` is its OWN independent flex container, so nothing keeps a given
// column's width consistent from one row to the next (unlike a real
// `<table>`, which has a single shared column-tracking layout algorithm).
// That gap normally hides behind equal `flex: 1 0 0px` columns (every row
// divides 100% of the same width evenly, so it "looks" aligned — just badly
// truncated). Fixing the truncation (`min-width: min-content` in
// _react-table.scss) let each row size its own cells off its own content,
// which broke that accidental alignment: the same logical column now
// renders a different width per row.
//
// This measures every cell's natural width per column across every row
// (header included), then pins every cell in that column to the widest one
// found, with flex-grow/flex-shrink disabled so nothing can silently
// re-diverge afterward — giving react-table the column synchronization a
// real `<table>` gets for free.
const DEBOUNCE_MS = 120;
let debounceTimer = null;

// Off-screen tables are skipped: measuring one costs a full synchronous
// layout, and a table nobody can see has nothing to align. It gets measured
// the moment it scrolls into view, because scrolling re-arms the scheduler.
function isVisible(tableEl) {
  const rect = tableEl.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  const viewportHeight = window.innerHeight || 0;
  return rect.bottom > -200 && rect.top < viewportHeight + 200;
}

function equalizeTable(tableEl) {
  const allCells = tableEl.querySelectorAll(".rt-th, .rt-td");
  allCells.forEach((cell) => {
    cell.style.flexBasis = "";
    cell.style.flexGrow = "";
    cell.style.flexShrink = "";
  });

  const rows = tableEl.querySelectorAll(".rt-thead .rt-tr, .rt-tbody .rt-tr");
  if (rows.length === 0) return;

  const columnCount = rows[0].children.length;
  const maxWidths = new Array(columnCount).fill(0);

  rows.forEach((row) => {
    if (row.children.length !== columnCount) return;
    Array.from(row.children).forEach((cell, i) => {
      const w = cell.getBoundingClientRect().width;
      if (w > maxWidths[i]) maxWidths[i] = w;
    });
  });

  rows.forEach((row) => {
    if (row.children.length !== columnCount) return;
    Array.from(row.children).forEach((cell, i) => {
      cell.style.flexGrow = "0";
      cell.style.flexShrink = "0";
      cell.style.flexBasis = `${Math.ceil(maxWidths[i])}px`;
    });
  });
}

function equalizeAllTables() {
  document.querySelectorAll(".ReactTable").forEach((tableEl) => {
    if (isVisible(tableEl)) equalizeTable(tableEl);
  });
}

export function initEqualizeReactTableColumns() {
  if (window.__algEqualizeReactTableInit) return;
  window.__algEqualizeReactTableInit = true;

  // The work itself runs inside a frame, so the read/write passes land in the
  // browser's own layout phase rather than forcing a synchronous reflow in the
  // middle of whatever else is running.
  const schedule = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      window.requestAnimationFrame(equalizeAllTables);
    }, DEBOUNCE_MS);
  };

  // Covers data/row changes (pagination, sort, filter, async load).
  //
  // Only mutations that could actually change a table are worth reacting to.
  // Observing `document.body` with `subtree` + `characterData` meant *every*
  // DOM change anywhere in the app re-armed a full re-measure of every table —
  // during boot, each preload's dispatch did exactly that, so the measuring
  // ran over and over while the page was already at its busiest.
  const touchesATable = (mutation) => {
    // A change inside a table that already exists.
    const target = mutation.target;
    const el = target.nodeType === 1 ? target : target.parentElement;
    if (el && el.closest && el.closest(".ReactTable")) return true;

    // Or a subtree containing a table being mounted for the first time — the
    // added node's parent is outside any table, so `closest` alone misses it.
    const added = mutation.addedNodes || [];
    for (let i = 0; i < added.length; i++) {
      const node = added[i];
      if (node.nodeType !== 1) continue;
      if (
        node.classList.contains("ReactTable") ||
        (node.querySelector && node.querySelector(".ReactTable"))
      ) {
        return true;
      }
    }
    return false;
  };

  const observer = new MutationObserver((mutations) => {
    for (let i = 0; i < mutations.length; i++) {
      if (touchesATable(mutations[i])) {
        schedule();
        return;
      }
    }
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  // Covers layout changes a DOM mutation wouldn't catch on its own — e.g. a
  // tab switcher that toggles visibility via className on an already-mounted
  // panel, or a viewport resize changing wrapped-header text width.
  window.addEventListener("resize", schedule);
  window.addEventListener("click", schedule, true);

  schedule();
}
